import { AxiosResponse } from 'axios';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';

import { SettingsNationalHolidays } from '../domain/SettingsNationalHolidays';
import { getExcludeDates } from '../domain/chain/date';
import { validateChainStoreShuKadoGraphParams } from '../domain/chain/validation';
import { ChainReportsFormConditions } from '../domain/chainReportsFormConditions';
import { ChainStoreReportsSettingDateRangeParams } from '../domain/chainStoreReportsSettingDateRangeParams';
import { DataKadoGraph, DataKadoGraphParams } from '../domain/dataKadoGraph';
import { resolveShuSectionKey } from '../domain/shu';

import {
  DataChainStoreShuKadoGraphActionCreators,
  DataChainStoreShuKadoGraphActionTypes,
  FetchDataChainStoreShuKadoGraphAction,
  SearchDataChainStoreShuKadoGraphAction,
  SearchDataChainStoreShuKadoGraphHallAction,
  SearchDataChainStoreShuKadoGraphKadoTypeAction,
  dataChainStoreShuKadoGraphSearchConditionSelector,
} from '../redux/server/dataChainStoreShuKadoGraph';
import {
  ChainStoreSettingActionCreators,
  chainReportsComparativeSectionSelector,
  chainStoreReportsSearchConditionSelector,
  chainStoreReportsSelectedDateRangeParamsSelector,
} from '../redux/ui/chainStoreReportsSetting';
import { Api, buildConfig } from '../utils/api';
import { ComparativeSection } from '../utils/comparativeSectionUtil';
import { customSelect } from '../utils/customSelect';
import { getHallsAndAreas } from '../utils/getHallsAndAreas';
import { selectShu2SearchCondition } from '../utils/shu';
import { searchConditionToDateRangeParams } from './chainStoreReportsSettingSaga';
import { handleErrorSaga } from './errorSaga';
import { ensureNationalHolidaysWhenNeeded } from './nationalHolidaysHelpers';

type KadoGraphRestParams = ReturnType<typeof getExcludeDates>;

/**
 * 時間帯別稼働数API用のパラメータを組み立てる
 * ymdList/ymdComparisonList と startDate/endDate 系は同時に使用不可のため排他で設定する
 */
function buildKadoGraphFetchParams(
  params: DataKadoGraphParams,
  restParams: KadoGraphRestParams,
  comparativeSection: ComparativeSection | undefined,
  nationalHolidays: SettingsNationalHolidays | undefined,
  formConditions: ChainReportsFormConditions,
  dateRangeParams: ChainStoreReportsSettingDateRangeParams
): DataKadoGraphParams {
  // 平日/土日祝比較かつパラメータに日付リストが無い（お気に入り復元等）ときのみ、検索条件から再計算する
  const needsRecalc =
    comparativeSection === '平日/土日祝比較' &&
    (!params.ymdList?.length || !params.ymdComparisonList?.length);

  // 再計算時は検索条件・期間・祝日から、ymdList（平日）と ymdComparisonList（土日祝）を算出
  const dateParams = needsRecalc
    ? searchConditionToDateRangeParams(
        dateRangeParams.dateRange,
        dateRangeParams.dateUnit,
        dateRangeParams.isComparison,
        formConditions,
        comparativeSection,
        nationalHolidays?.holidays
      )
    : null;

  // 再計算結果があればそれを使い、なければパラメータの日付リストをそのまま使う
  const ymdList = dateParams?.ymdList ?? params.ymdList;
  const ymdComparisonList =
    dateParams?.ymdComparisonList ?? params.ymdComparisonList;

  // ymd系のいずれかが1件以上あれば、APIにはymdList/ymdComparisonListを送り、そうでなければstartDate/endDateを送る
  const hasYmdList = (ymdList?.length ?? 0) > 0;
  const hasYmdComparisonList = (ymdComparisonList?.length ?? 0) > 0;
  const useYmdLists = hasYmdList || hasYmdComparisonList;

  return useYmdLists
    ? {
        ...restParams,
        ...(hasYmdList ? { ymdList } : {}),
        ...(hasYmdComparisonList ? { ymdComparisonList } : {}),
      }
    : {
        ...restParams,
        startDate: params.startDate,
        endDate: params.endDate,
        startComparisonDate: params.startComparisonDate,
        endComparisonDate: params.endComparisonDate,
      };
}

/**
 * 時間帯別稼働数データ取得処理
 * 平日/土日祝比較の場合は、平日をymdList・土日祝をymdComparisonListとしてAPIに送る
 */
export function* fetchDataChainStoreShuKadoGraphSaga(
  api: Api,
  action: FetchDataChainStoreShuKadoGraphAction
) {
  const { shuCode, params } = action.payload;

  // 取得（開く/再取得）開始時に、この種別の時間帯別稼動グラフの開閉アニメーションを有効化しておく。
  // 直前に閉じアニメーションでフラグがfalseのまま残っていても、再度開くときに正しくアニメーションさせる
  yield put(
    ChainStoreSettingActionCreators.selectChainStoreShuKadoGraphAnimationEnabledAction(
      shuCode,
      true
    )
  );

  const restParams = getExcludeDates(params);

  // 比較区分を取得（平日/土日祝比較の判定に必要）
  const comparativeSection = yield* customSelect(
    chainReportsComparativeSectionSelector
  );

  // 平日/土日祝比較の場合は祝日取得が必要
  const { nationalHolidays, shouldAbort } =
    yield* ensureNationalHolidaysWhenNeeded(comparativeSection);
  if (shouldAbort) {
    return;
  }

  // 検索条件を取得
  const formConditions = yield* customSelect(
    chainStoreReportsSearchConditionSelector
  );

  // 期間パラメータを取得
  const dateRangeParams = yield* customSelect(
    chainStoreReportsSelectedDateRangeParamsSelector
  );

  // API用のパラメータを組み立てる
  const newParams = buildKadoGraphFetchParams(
    params,
    restParams,
    comparativeSection,
    nationalHolidays,
    formConditions,
    dateRangeParams
  );

  try {
    // APIリクエスト前のパラメータバリデーション
    validateChainStoreShuKadoGraphParams(newParams);

    yield put(
      DataChainStoreShuKadoGraphActionCreators.fetchDataChainStoreShuKadoGraphRequestAction(
        shuCode
      )
    );
    const response: AxiosResponse<DataKadoGraph> = yield call(
      api.get,
      '/data/hall/shu/kado/graph',
      buildConfig(newParams)
    );
    yield put(
      DataChainStoreShuKadoGraphActionCreators.fetchDataChainStoreShuKadoGraphSuccessAction(
        shuCode,
        response.data
      )
    );
  } catch (error: unknown) {
    yield put(
      DataChainStoreShuKadoGraphActionCreators.renewDataChainStoreShuKadoGraphAction()
    );
    yield fork(handleErrorSaga, error);
  }
}

function* handleFetchSettingsOptionsPlanSaga(api: Api) {
  yield takeEvery(
    DataChainStoreShuKadoGraphActionTypes.FETCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
    fetchDataChainStoreShuKadoGraphSaga,
    api
  );
}

/**
 * クリックしたセルの時間帯別稼働数データを取得する
 */
function* searchChainStoreShuKadoGraphSaga(
  action: SearchDataChainStoreShuKadoGraphAction
) {
  const { selectOption, shuOption } = action.payload;
  const shuCode = resolveShuSectionKey(shuOption);

  // 検索フォームの検索条件を取得
  const searchCondition: ChainReportsFormConditions = yield select(
    chainStoreReportsSearchConditionSelector
  );

  // 取得済みのデータの検索条件
  const graphSearchCondition: DataKadoGraphParams = yield select(
    dataChainStoreShuKadoGraphSearchConditionSelector(shuCode)
  );

  yield put(
    DataChainStoreShuKadoGraphActionCreators.fetchDataChainStoreShuKadoGraphAction(
      shuCode,
      {
        ...graphSearchCondition,
        // MEMO: 種別がある場合は種別コードをセットし、ない場合（店舗全体実績）は種別の指定を空にする
        ...(shuOption ? selectShu2SearchCondition(shuOption) : {}),
        ...getHallsAndAreas(searchCondition, selectOption),
        ymdList: searchCondition.ymdList,
        ymdComparisonList: searchCondition.ymdComparisonList,
      }
    )
  );
}

/**
 * 指定した時間帯別稼働数の店舗のデータを再取得する
 */
function* searchChainStoreShuKadoGraphHallSaga(
  action: SearchDataChainStoreShuKadoGraphHallAction
) {
  const { shuOption, selectOption } = action.payload;
  const shuCode = resolveShuSectionKey(shuOption);

  // 検索フォームの検索条件を取得
  const searchCondition: ChainReportsFormConditions = yield select(
    chainStoreReportsSearchConditionSelector
  );

  // 取得済みのデータの検索条件
  const graphSearchCondition: DataKadoGraphParams = yield select(
    dataChainStoreShuKadoGraphSearchConditionSelector(shuCode)
  );

  yield put(
    DataChainStoreShuKadoGraphActionCreators.fetchDataChainStoreShuKadoGraphAction(
      shuCode,
      {
        ...graphSearchCondition,
        ...getHallsAndAreas(searchCondition, selectOption),
        // 店舗変更時も検索条件の日付リスト（平日/土日祝）を維持する
        ymdList: searchCondition.ymdList,
        ymdComparisonList: searchCondition.ymdComparisonList,
      }
    )
  );
}

/**
 * 指定した時間帯別稼働数の稼働数・稼働率のデータを再取得する
 */
function* searchChainStoreShuKadoGraphKadoTypeSaga(
  action: SearchDataChainStoreShuKadoGraphKadoTypeAction
) {
  const { shuOption, kadoFieldType } = action.payload;
  const shuCode = resolveShuSectionKey(shuOption);

  // 取得済みのデータの検索条件
  const searchCondition: DataKadoGraphParams = yield select(
    dataChainStoreShuKadoGraphSearchConditionSelector(shuCode)
  );

  yield put(
    DataChainStoreShuKadoGraphActionCreators.fetchDataChainStoreShuKadoGraphAction(
      shuCode,
      {
        ...searchCondition,
        kadoFieldType,
      }
    )
  );
}

/**
 * 検索関連の処理
 */
function* handleSearchSaga() {
  // セルの時間帯別稼働数ボタンクリック時
  yield takeEvery(
    DataChainStoreShuKadoGraphActionTypes.SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH,
    searchChainStoreShuKadoGraphSaga
  );
  // 時間帯別稼働数の店舗変更時
  yield takeEvery(
    DataChainStoreShuKadoGraphActionTypes.SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_HALL,
    searchChainStoreShuKadoGraphHallSaga
  );
  // 時間帯別稼働数の稼働数・稼働率変更時
  yield takeEvery(
    DataChainStoreShuKadoGraphActionTypes.SEARCH_DATA_CHAIN_STORE_SHU_KADO_GRAPH_KADO_TYPE,
    searchChainStoreShuKadoGraphKadoTypeSaga
  );
}

export function* dataChainStoreShuKadoGraphSaga(context: { api: Api }) {
  yield fork(handleFetchSettingsOptionsPlanSaga, context.api);
  yield fork(handleSearchSaga);
}
